import { supabase } from '@/shared/api/supabase';
import {
	Constants,
	type Database,
	type Json,
} from '@/shared/types/database.types';

type UpsertWebPushDeviceParams = {
	endpoint: string;
	platform: string;
	subscription: Json;
	userId: string;
};

const api = {
	getUserProfile: async () => {
		return await supabase.from('profiles').select('*').single();
	},

	checkUsernameExists: async (username: string) => {
		const { count } = await supabase
			.from('profiles')
			.select('*', { count: 'exact', head: true })
			.eq('username', username);
		return (count ?? 0) > 0;
	},

	generateRandomUsername: async () => {
		const { data } = await supabase.rpc('generate_random_nickname');
		return data;
	},

	updateUsername: async (username: string) => {
		const {
			data: { user },
		} = await supabase.auth.getUser();

		if (!user) {
			throw new Error('User not found');
		}

		return await supabase
			.from('profiles')
			.update({ username })
			.eq('id', user.id);
	},

	getGasStationList: async () => {
		return await supabase
			.from('gas_stations')
			.select(
				'id, brand, station_name, road_number_address, tel, is_quality_certified, coor_x, coor_y, region',
			);
	},

	getLatestOilPricebyFuelType: async (
		stationId: string,
		fuelType: (typeof Constants.public.Enums.fuel_type)[number],
	) => {
		return await supabase
			.from('oil_prices')
			.select('*')
			.eq('station_id', stationId)
			.eq('type', fuelType)
			.order('updated_at', { ascending: false })
			.limit(1)
			.single();
	},

	getLatestOilPrice: async (stationId: string) => {
		return await supabase.rpc('get_latest_oil_prices', {
			p_station_id: stationId,
		});
	},

	getNearbyGasStations: async (
		latitude: number,
		longitude: number,
		radius: number,
	) => {
		return await supabase.rpc('nearby_gas_stations', {
			lat: latitude,
			long: longitude,
			radius_meters: radius,
		});
	},

	searchGasStations: async (
		query: string,
		latitude?: number,
		longitude?: number,
		limit: number = 20,
	) => {
		return await supabase.rpc('search_gas_stations', {
			search_text: query,
			lat: latitude,
			long: longitude,
			limit_count: limit,
		});
	},

	getGasStationWithinBoundingBox: async (
		minLat: number,
		maxLat: number,
		minLng: number,
		maxLng: number,
	) => {
		return await supabase.rpc('gas_stations_in_view', {
			min_lat: minLat,
			max_lat: maxLat,
			min_lng: minLng,
			max_lng: maxLng,
		});
	},

	getCarModelById: async (id: string) => {
		return await supabase
			.from('car_information')
			.select('*')
			.eq('id', id)
			.single();
	},

	upsertUserCarInfo: async (
		carInfo: Database['public']['Tables']['cars']['Insert'],
	) => {
		return await supabase.from('cars').upsert(carInfo);
	},

	deleteUserCarInfo: async () => {
		const {
			data: { user },
		} = await supabase.auth.getUser();

		if (!user) {
			throw new Error('User not found');
		}

		return await supabase.from('cars').delete().eq('user_id', user.id);
	},

	getUserCarInfo: async () => {
		return await supabase
			.from('cars')
			.select(`*, car_information(*)`)
			.maybeSingle();
	},

	searchCarInformation: async (searchText: string, limit: number = 20) => {
		return await supabase.rpc('search_car_information', {
			search_text: searchText,
			limit_count: limit,
		});
	},

	toggleSavedGasStation: async (stationId: string) => {
		const { data } = await supabase.rpc('toggle_bookmark', {
			p_station_id: stationId,
		});
		return data ?? false;
	},
	getUserSavedGasStations: async () => {
		return await supabase.from('favorites').select('*');
	},

	getFavoriteStationsWithDetails: async () => {
		return await supabase.from('favorites').select(`
			id,
			station_id,
			gas_stations (
				brand,
				station_name,
				road_number_address,
				station_logo
			)
		`);
	},

	checkIsSavedGasStation: async (stationId: string) => {
		const { count } = await supabase
			.from('favorites')
			.select('*', { count: 'exact', head: true })
			.eq('station_id', stationId);

		return (count ?? 0) > 0;
	},

	getWebPushDeviceByEndpoint: async (endpoint: string) => {
		return await supabase
			.from('notification_devices')
			.select('*')
			.eq('channel', 'web_push')
			.eq('endpoint', endpoint)
			.eq('is_active', true)
			.maybeSingle();
	},

	upsertWebPushDevice: async ({
		endpoint,
		platform,
		subscription,
		userId,
	}: UpsertWebPushDeviceParams) => {
		return await supabase.from('notification_devices').upsert(
			{
				channel: 'web_push',
				endpoint,
				is_active: true,
				last_seen_at: new Date().toISOString(),
				platform,
				subscription,
				updated_at: new Date().toISOString(),
				user_id: userId,
			},
			{ onConflict: 'user_id,endpoint' },
		);
	},

	deactivateWebPushDevice: async (endpoint: string) => {
		return await supabase
			.from('notification_devices')
			.update({
				is_active: false,
				updated_at: new Date().toISOString(),
			})
			.eq('channel', 'web_push')
			.eq('endpoint', endpoint);
	},

	getStationReviews: async (stationId: string) => {
		return await supabase
			.from('reviews')
			.select('*')
			.eq('station_id', stationId);
	},

	getStationReviewsByUser: async (userId: string) => {
		return await supabase.from('reviews').select('*').eq('user_id', userId);
	},

	updateStationReview: async (
		reviewId: string,
		content: string,
		score: number,
	) => {
		return await supabase
			.from('reviews')
			.update({ content, score })
			.eq('id', reviewId);
	},

	deleteStationReview: async (reviewId: string) => {
		return await supabase.from('reviews').delete().eq('id', reviewId);
	},
};

export default api;
